import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type React from 'react'
import { ChevronDown, ChevronRight, Eye, EyeOff, Palette, RotateCcw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { GpuEffectDefinition } from '@/infrastructure/gpu/effects'
import { cn } from '@/shared/ui/cn'
import { PropertyRow } from '@/shared/ui/property-controls'
import { buildMonotoneCurveSvgPath } from '@/shared/utils/curve-spline'
import {
  buildGpuCurvesChannelPoints,
  getDefaultGpuCurvesChannelControl,
  getGpuCurvesDefaultParams,
  getGpuCurvesDraftParams,
  GPU_CURVES_CHANNELS,
  GPU_CURVES_POINT_MAX_X,
  GPU_CURVES_POINT_MIN_GAP,
  GPU_CURVES_POINT_MIN_X,
  readGpuCurvesChannelControl,
  toGpuCurvesChannelParamUpdates,
  type GpuCurvesChannelControl,
  type GpuCurvesChannelKey,
} from '@/shared/utils/gpu-curves'
import type { GpuEffect, ItemEffect } from '@/types/effects'
import { translateEffectUiText } from './effect-ui-i18n'

interface GpuCurvesPanelProps {
  effect: ItemEffect
  gpuEffect: GpuEffect
  definition: GpuEffectDefinition
  collapsible?: boolean
  onEditInColor?: () => void
  layout?: 'sidebar' | 'dock'
  onParamChange: (effectId: string, paramKey: string, value: number | boolean | string) => void
  onParamLiveChange: (effectId: string, paramKey: string, value: number | boolean | string) => void
  onParamsBatchChange: (
    effectId: string,
    updates: Record<string, number | boolean | string>,
  ) => void
  onParamsBatchLiveChange: (
    effectId: string,
    updates: Record<string, number | boolean | string>,
  ) => void
  onReset: (effectId: string) => void
  onToggle: (effectId: string) => void
  onRemove: (effectId: string) => void
}

type DraftParams = Record<string, number>
type PointKey = keyof GpuCurvesChannelControl

const CURVE_SIZE = 230
const CHANNELS: Array<{ key: GpuCurvesChannelKey; label: string; color: string }> = [
  { key: 'master', label: '主通道', color: '#e5e7eb' },
  { key: 'red', label: '红', color: '#ef4444' },
  { key: 'green', label: '绿', color: '#22c55e' },
  { key: 'blue', label: '蓝', color: '#3b82f6' },
]

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function getResetParamsForChannel(channel: GpuCurvesChannelKey): Record<string, number> {
  return toGpuCurvesChannelParamUpdates(channel, getDefaultGpuCurvesChannelControl())
}

export const GpuCurvesPanel = memo(function GpuCurvesPanel({
  effect,
  gpuEffect,
  definition,
  collapsible = false,
  onEditInColor,
  layout = 'sidebar',
  onParamsBatchChange,
  onParamsBatchLiveChange,
  onReset,
  onToggle,
  onRemove,
}: GpuCurvesPanelProps) {
  const isDock = layout === 'dock'
  const [collapsed, setCollapsed] = useState(collapsible ? true : false)
  const svgRef = useRef<SVGSVGElement>(null)
  const [activeChannel, setActiveChannel] = useState<GpuCurvesChannelKey>('master')
  const [dragging, setDragging] = useState(false)
  const [draft, setDraft] = useState<DraftParams>(() => getGpuCurvesDraftParams(gpuEffect.params))
  const draftRef = useRef(draft)

  useEffect(() => {
    draftRef.current = draft
  }, [draft])

  useEffect(() => {
    if (!dragging) {
      setDraft(getGpuCurvesDraftParams(gpuEffect.params))
    }
  }, [dragging, gpuEffect.params])

  const isDefault = useMemo(() => {
    const defaults = getGpuCurvesDefaultParams()
    return Object.entries(defaults).every(([key, value]) => draft[key] === value)
  }, [draft])

  const activeChannelMeta = CHANNELS.find((channel) => channel.key === activeChannel)!
  const activeControl = useMemo(
    () => readGpuCurvesChannelControl(draft, activeChannel),
    [activeChannel, draft],
  )
  const handlePoints = useMemo(
    () => buildGpuCurvesChannelPoints(activeControl).slice(1, 3),
    [activeControl],
  )

  const curvePaths = useMemo(
    () =>
      Object.fromEntries(
        GPU_CURVES_CHANNELS.map((channel) => [
          channel,
          buildMonotoneCurveSvgPath(
            buildGpuCurvesChannelPoints(readGpuCurvesChannelControl(draft, channel)),
            CURVE_SIZE,
            CURVE_SIZE,
          ),
        ]),
      ) as Record<GpuCurvesChannelKey, string>,
    [draft],
  )

  const dragRef = useRef<{
    channel: GpuCurvesChannelKey
    pointKey: PointKey
  } | null>(null)

  const getNormalizedPointFromClient = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current
    if (!svg) return null
    const rect = svg.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return null
    return {
      x: clamp((clientX - rect.left) / rect.width, 0, 1),
      y: clamp(1 - (clientY - rect.top) / rect.height, 0, 1),
    }
  }, [])

  const computeDragUpdates = useCallback(
    (clientX: number, clientY: number) => {
      const state = dragRef.current
      if (!state) return null

      const position = getNormalizedPointFromClient(clientX, clientY)
      if (!position) return null

      const current = readGpuCurvesChannelControl(draftRef.current, state.channel)
      const otherPoint = state.pointKey === 'shadow' ? current.highlight : current.shadow

      const nextControl: GpuCurvesChannelControl = {
        ...current,
        [state.pointKey]: {
          x:
            state.pointKey === 'shadow'
              ? clamp(position.x, GPU_CURVES_POINT_MIN_X, otherPoint.x - GPU_CURVES_POINT_MIN_GAP)
              : clamp(position.x, otherPoint.x + GPU_CURVES_POINT_MIN_GAP, GPU_CURVES_POINT_MAX_X),
          y: position.y,
        },
      }

      return toGpuCurvesChannelParamUpdates(state.channel, nextControl)
    },
    [getNormalizedPointFromClient],
  )

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const updates = computeDragUpdates(event.clientX, event.clientY)
      if (!updates) return
      setDraft((prev) => ({ ...prev, ...updates }))
      onParamsBatchLiveChange(effect.id, updates)
    }

    const handleMouseUp = (event: MouseEvent) => {
      const state = dragRef.current
      if (!state) return

      const updates =
        computeDragUpdates(event.clientX, event.clientY) ??
        toGpuCurvesChannelParamUpdates(
          state.channel,
          readGpuCurvesChannelControl(draftRef.current, state.channel),
        )

      setDraft((prev) => ({ ...prev, ...updates }))
      onParamsBatchChange(effect.id, updates)
      dragRef.current = null
      setDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [computeDragUpdates, effect.id, onParamsBatchChange, onParamsBatchLiveChange])

  const handlePointMouseDown = useCallback(
    (event: React.MouseEvent, pointKey: PointKey) => {
      if (!effect.enabled) return
      event.preventDefault()
      event.stopPropagation()
      dragRef.current = { channel: activeChannel, pointKey }
      setDragging(true)
    },
    [activeChannel, effect.enabled],
  )

  const handleResetChannel = useCallback(() => {
    const updates = getResetParamsForChannel(activeChannel)
    setDraft((prev) => ({ ...prev, ...updates }))
    onParamsBatchChange(effect.id, updates)
  }, [activeChannel, effect.id, onParamsBatchChange])

  const showBody = !(collapsible && collapsed)

  const channelControls = (
    <div
      className={cn(
        'flex items-center gap-1',
        isDock ? 'min-w-0 flex-nowrap justify-end overflow-x-auto' : 'flex-wrap justify-end',
      )}
    >
      {CHANNELS.map((channel) => (
        <Button
          key={channel.key}
          variant={activeChannel === channel.key ? 'secondary' : 'outline'}
          size="sm"
          className={cn('shrink-0', isDock ? 'h-6 px-2 text-[10px]' : 'h-7 px-2 text-xs')}
          onClick={() => setActiveChannel(channel.key)}
        >
          {channel.label}
        </Button>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className={cn('shrink-0', isDock ? 'h-6 px-2 text-[10px]' : 'h-7 px-2 text-xs')}
        onClick={handleResetChannel}
        disabled={!effect.enabled}
      >
        重置通道
      </Button>
    </div>
  )

  return (
    <div className={cn('space-y-0', isDock && 'flex h-full min-h-0 flex-col overflow-hidden')}>
      <PropertyRow label={translateEffectUiText(definition.name)}>
        <div className="flex min-w-0 w-full items-center justify-end gap-1">
          {onEditInColor ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0"
              onClick={onEditInColor}
              title="进入调色工作区"
              aria-label="进入调色工作区"
            >
              <Palette className="h-3 w-3" />
            </Button>
          ) : null}
          {collapsible ? (
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              className="flex flex-1 items-center gap-1 min-w-0 text-left text-xs text-muted-foreground hover:text-foreground"
            >
              {collapsed ? (
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
              )}
              <span className="truncate">{translateEffectUiText(definition.name)}</span>
              {!isDefault ? (
                <span
                  className="ml-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-6 w-6 flex-shrink-0', isDefault && 'opacity-30')}
            onClick={() => onReset(effect.id)}
            title={translateEffectUiText('Reset to defaults')}
            disabled={isDefault}
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={() => onToggle(effect.id)}
            title={translateEffectUiText(effect.enabled ? 'Disable effect' : 'Enable effect')}
          >
            {effect.enabled ? (
              <Eye className="h-3 w-3" />
            ) : (
              <EyeOff className="h-3 w-3 text-muted-foreground" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={() => onRemove(effect.id)}
            title={translateEffectUiText('Remove effect')}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </PropertyRow>

      {showBody && (
        <>
          {isDock ? (
            <div className="flex h-9 shrink-0 items-center justify-between gap-2 px-2">
              <span className="shrink-0 text-[11px] text-muted-foreground">通道</span>
              {channelControls}
            </div>
          ) : (
            <PropertyRow label="通道">{channelControls}</PropertyRow>
          )}

      <div className={cn('px-2', isDock && 'flex min-h-0 flex-1 pb-2')}>
        <div
          className={cn(
            'relative overflow-hidden rounded border border-border/70 bg-black/50',
            isDock && 'flex min-h-0 flex-1',
          )}
        >
          <svg
            ref={svgRef}
            viewBox={`0 0 ${CURVE_SIZE} ${CURVE_SIZE}`}
            preserveAspectRatio={isDock ? 'none' : 'xMidYMid meet'}
            className={cn(
              isDock ? 'h-full w-full' : 'aspect-square w-full',
              effect.enabled ? 'cursor-move' : 'cursor-default',
            )}
          >
            {[0.25, 0.5, 0.75].map((grid) => (
              <g key={grid}>
                <line
                  x1={grid * CURVE_SIZE}
                  y1={0}
                  x2={grid * CURVE_SIZE}
                  y2={CURVE_SIZE}
                  stroke="rgba(148,163,184,0.22)"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
                <line
                  x1={0}
                  y1={grid * CURVE_SIZE}
                  x2={CURVE_SIZE}
                  y2={grid * CURVE_SIZE}
                  stroke="rgba(148,163,184,0.22)"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            ))}

            <path
              d={`M 0 ${CURVE_SIZE} L ${CURVE_SIZE} 0`}
              stroke="rgba(148,163,184,0.35)"
              strokeWidth={1}
              fill="none"
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
            />

            {activeChannel === 'master' && (
              <>
                <path
                  d={curvePaths.red}
                  stroke="#ef4444"
                  strokeWidth={1.2}
                  fill="none"
                  opacity={0.35}
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d={curvePaths.green}
                  stroke="#22c55e"
                  strokeWidth={1.2}
                  fill="none"
                  opacity={0.35}
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d={curvePaths.blue}
                  stroke="#3b82f6"
                  strokeWidth={1.2}
                  fill="none"
                  opacity={0.35}
                  vectorEffect="non-scaling-stroke"
                />
              </>
            )}

            {activeChannel !== 'master' && (
              <path
                d={curvePaths.master}
                stroke="rgba(229,231,235,0.35)"
                strokeWidth={1.2}
                fill="none"
                strokeDasharray="5 4"
                vectorEffect="non-scaling-stroke"
              />
            )}

            <path
              d={curvePaths[activeChannel]}
              stroke={activeChannelMeta.color}
              strokeWidth={2}
              fill="none"
              vectorEffect="non-scaling-stroke"
            />

            {handlePoints.map((point, index) => {
              const pointKey = index === 0 ? 'shadow' : 'highlight'
              const x = point.x * CURVE_SIZE
              const y = (1 - point.y) * CURVE_SIZE

              return (
                <g key={pointKey}>
                  <line
                    x1={x}
                    y1={CURVE_SIZE}
                    x2={x}
                    y2={y}
                    stroke={activeChannelMeta.color}
                    strokeWidth={1}
                    opacity={0.18}
                    vectorEffect="non-scaling-stroke"
                  />
                  <circle
                    cx={x}
                    cy={y}
                    r={6}
                    fill={activeChannelMeta.color}
                    stroke="rgba(3,7,18,0.95)"
                    strokeWidth={1.5}
                    className={effect.enabled ? 'cursor-move' : 'pointer-events-none'}
                    onMouseDown={(event) => handlePointMouseDown(event, pointKey)}
                  />
                </g>
              )
            })}
          </svg>
        </div>
      </div>

      {!isDock && (
        <div className="px-2 pb-1 text-center text-[10px] text-muted-foreground">
          拖动两个控制点来调整{activeChannelMeta.label}曲线，左侧压暗、右侧提亮即可得到常见的 S 曲线。
        </div>
      )}
        </>
      )}
    </div>
  )
})
